import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse } from './baseApi';

export interface EndgoodCostMasterDto {
  id: number;
  endgoodId: number;
  endgoodName?: string;
  cost: number;
  effectiveDate: string;
  createdAt?: string;
  createdBy?: number;
}

export interface EndgoodCostMasterCreateRequest {
  endgoodId: number;
  cost: number;
  effectiveDate: string;
}

export interface EndgoodCostMasterUpdateRequest extends EndgoodCostMasterCreateRequest {
  id: number;
}

class EndgoodCostMasterApiService extends BaseApiService {
  async create(data: EndgoodCostMasterCreateRequest, branchId: number): Promise<ApiResponse<EndgoodCostMasterDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodCostMasterDto>>(
        API_ENDPOINTS.ENDGOOD_COST_MASTER.CREATE(branchId),
        { method: HTTP_METHODS.POST, body: JSON.stringify(data) }
      );
      if (response.success) toast.success('Endgood cost added successfully');
      return response;
    } catch (error) {
      toast.error('Failed to add endgood cost');
      throw error;
    }
  }

  async getById(id: string, branchId: number): Promise<ApiResponse<EndgoodCostMasterDto>> {
    try {
      return await this.makeRequest<ApiResponse<EndgoodCostMasterDto>>(
        API_ENDPOINTS.ENDGOOD_COST_MASTER.DETAIL(id, branchId),
        { method: HTTP_METHODS.GET }
      );
    } catch (error) {
      toast.error('Failed to fetch endgood cost');
      throw error;
    }
  }

  async getByEndgoodId(endgoodId: string, branchId: number): Promise<ApiResponse<EndgoodCostMasterDto[]>> {
    try {
      return await this.makeRequest<ApiResponse<EndgoodCostMasterDto[]>>(
        API_ENDPOINTS.ENDGOOD_COST_MASTER.BY_ENDGOOD(endgoodId, branchId),
        { method: HTTP_METHODS.GET }
      );
    } catch (error) {
      toast.error('Failed to fetch costs for endgood');
      throw error;
    }
  }

  async getCostAsOf(endgoodId: string, asOfDate: string, branchId: number): Promise<ApiResponse<EndgoodCostMasterDto>> {
    try {
      return await this.makeRequest<ApiResponse<EndgoodCostMasterDto>>(
        API_ENDPOINTS.ENDGOOD_COST_MASTER.AS_OF(endgoodId, branchId, asOfDate),
        { method: HTTP_METHODS.GET }
      );
    } catch (error) {
      toast.error('Failed to fetch cost as of date');
      throw error;
    }
  }

  async update(id: string, data: EndgoodCostMasterUpdateRequest, branchId: number): Promise<ApiResponse<EndgoodCostMasterDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodCostMasterDto>>(
        API_ENDPOINTS.ENDGOOD_COST_MASTER.UPDATE(id, branchId),
        { method: HTTP_METHODS.PUT, body: JSON.stringify(data) }
      );
      if (response.success) toast.success('Endgood cost updated successfully');
      return response;
    } catch (error) {
      toast.error('Failed to update endgood cost');
      throw error;
    }
  }

  async delete(id: string, branchId: number): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.ENDGOOD_COST_MASTER.DELETE(id, branchId),
        { method: HTTP_METHODS.DELETE }
      );
      if (response.success) toast.success('Endgood cost deleted');
      return response;
    } catch (error) {
      toast.error('Failed to delete endgood cost');
      throw error;
    }
  }
}

export const endgoodCostMasterApiService = new EndgoodCostMasterApiService();
